import React, { useState } from 'react';
import { 
  Upload, 
  Trash2, 
  Type, 
  Sparkles, 
  FileText, 
  FileSpreadsheet, 
  Paintbrush, 
  Sun, 
  Moon, 
  BookOpen, 
  Terminal 
} from 'lucide-react';

interface BrandingSectionProps {
  editing: boolean;
  formData: {
    companyName: string;
    logoUrl: string;
    colorScheme: string;
    mainColor: string;
    bgColor: string;
    fontFamily: string;
    themeMode: string;
  };
  setFormData: (val: any) => void;
}

const FONT_OPTIONS = [
  { id: 'Inter', name: 'Inter Sans', desc: 'Modern, highly clean, corporate standard' },
  { id: 'Space Grotesk', name: 'Space Grotesk', desc: 'Geo-sans, futuristic, tech-forward' },
  { id: 'Playfair Display', name: 'Playfair Display', desc: 'Traditional serif, editorial elegance' },
  { id: 'JetBrains Mono', name: 'JetBrains Mono', desc: 'Technical, tabular precision' }
];

const THEME_MODES = [
  { id: 'light', name: 'Light Document', desc: 'Standard high-contrast sheet', icon: Sun, class: 'bg-white border-gray-200 text-gray-800' },
  { id: 'ambientDark', name: 'Ambient Dark', desc: 'High-tech dark paper layout', icon: Moon, class: 'bg-slate-900 border-slate-800 text-slate-100' },
  { id: 'sepia', name: 'Warm Sepia', desc: 'Ink-on-paper traditional feel', icon: BookOpen, class: 'bg-[#faf6ee] border-[#ebd9c3] text-[#433422]' },
  { id: 'cyber', name: 'Cyber Tech', desc: 'Futuristic terminal theme', icon: Terminal, class: 'bg-black border-emerald-950 text-emerald-400 font-mono' }
];

const PRESET_SCHEMES = [
  { id: 'sapphire', name: 'Sapphire Corporate', mainColor: '#1e3a8a', bgColor: '#f8fafc', preview: 'bg-blue-800' },
  { id: 'emerald', name: 'Emerald Growth', mainColor: '#065f46', bgColor: '#f0fdf4', preview: 'bg-emerald-800' },
  { id: 'slate', name: 'Classic Slate', mainColor: '#334155', bgColor: '#f8fafc', preview: 'bg-slate-700' },
  { id: 'crimson', name: 'Royal Crimson', mainColor: '#991b1b', bgColor: '#fef2f2', preview: 'bg-red-800' },
  { id: 'amethyst', name: 'Creative Amethyst', mainColor: '#6d28d9', bgColor: '#faf5ff', preview: 'bg-purple-800' },
  { id: 'amber', name: 'Amber Trust', mainColor: '#92400e', bgColor: '#fffbeb', preview: 'bg-amber-800' },
  { id: 'custom', name: 'Custom Brand Palette', mainColor: '#4f46e5', bgColor: '#ffffff', preview: 'bg-gradient-to-r from-indigo-500 to-pink-500' }
];

export const BrandingSection: React.FC<BrandingSectionProps> = ({ editing, formData, setFormData }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [previewTab, setPreviewTab] = useState<'sow' | 'spreadsheet'>('sow');

  const getFontClass = (font: string) => {
    switch (font) {
      case 'Space Grotesk':
        return 'font-grotesk';
      case 'Playfair Display':
        return 'font-serif';
      case 'JetBrains Mono':
        return 'font-mono';
      default:
        return 'font-sans';
    }
  };

  const handleSchemeSelect = (scheme: typeof PRESET_SCHEMES[0]) => {
    if (!editing) return;
    if (scheme.id === 'custom') {
      setFormData({
        ...formData,
        colorScheme: scheme.id
      });
    } else {
      setFormData({
        ...formData,
        colorScheme: scheme.id,
        mainColor: scheme.mainColor,
        bgColor: scheme.bgColor
      });
    }
  };

  const handleFileChange = (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, JPG, or SVG).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormData({
        ...formData,
        logoUrl: reader.result as string
      });
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!editing) return;
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 text-left">
      {/* Controls Column */}
      <div className="lg:col-span-6 space-y-6">
        {/* Logo Section */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Corporate Logo</h4>
            {formData.logoUrl && editing && (
              <button
                type="button"
                onClick={() => setFormData({ ...formData, logoUrl: '' })}
                className="text-[10px] text-red-600 hover:text-red-700 font-bold flex items-center gap-1 transition-colors cursor-pointer"
              >
                <Trash2 className="w-3 h-3" /> Remove Logo
              </button>
            )}
          </div>

          {editing ? (
            <div className="space-y-3">
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                  isDragging 
                    ? 'border-indigo-600 bg-indigo-50/40' 
                    : 'border-gray-200 hover:border-gray-300 bg-gray-50/50'
                }`}
                onClick={() => {
                  const el = document.getElementById('logo-file-input');
                  if (el) el.click();
                }}
              >
                <input
                  id="logo-file-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      handleFileChange(e.target.files[0]);
                    }
                  }}
                  className="hidden"
                />
                {formData.logoUrl ? (
                  <div className="flex flex-col items-center space-y-2">
                    <img src={formData.logoUrl} alt="Logo Preview" className="h-12 max-w-[150px] object-contain" referrerPolicy="no-referrer" />
                    <p className="text-[10px] text-emerald-600 font-bold">Successfully loaded. Drag or click to change.</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center space-y-2">
                    <div className="p-2.5 bg-gray-100 rounded-lg text-gray-500">
                      <Upload className="w-5 h-5" />
                    </div>
                    <p className="text-xs font-bold text-gray-700">Drag logo here or click to upload</p>
                    <p className="text-[9px] text-gray-400">Supports PNG, JPG, or SVG. Automatically optimized.</p>
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-gray-400 uppercase">Or provide Logo URL directly</label>
                <input
                  className="w-full text-xs p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                  value={formData.logoUrl.startsWith('data:') ? '' : formData.logoUrl}
                  onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                  placeholder="https://example.com/logo.png"
                />
              </div>
            </div>
          ) : (
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {formData.logoUrl ? (
                  <img src={formData.logoUrl} alt="Logo" className="h-10 max-w-[120px] object-contain" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-10 h-10 bg-indigo-100 text-indigo-700 font-bold rounded-lg flex items-center justify-center text-sm">
                    {formData.companyName?.substring(0, 2).toUpperCase() || "RF"}
                  </div>
                )}
                <div>
                  <p className="text-xs font-bold text-gray-900">Corporate Identity</p>
                  <p className="text-[10px] text-gray-400">{formData.logoUrl ? 'Custom uploaded asset' : 'Standard initials fallback fallback'}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Font Family Selection */}
        <div className="space-y-3">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Typography Style</h4>
          <div className="grid grid-cols-2 gap-3">
            {FONT_OPTIONS.map((font) => (
              <button
                key={font.id}
                type="button"
                disabled={!editing}
                onClick={() => setFormData({ ...formData, fontFamily: font.id })}
                className={`p-3.5 border rounded-xl text-left transition-all ${
                  formData.fontFamily === font.id
                    ? 'border-indigo-600 bg-indigo-50/30 ring-2 ring-indigo-500/20'
                    : 'border-gray-200 hover:border-gray-300'
                } ${!editing && 'opacity-85'}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={`p-1 rounded bg-gray-100 text-gray-600 ${formData.fontFamily === font.id && 'bg-indigo-100 text-indigo-700'}`}>
                    <Type className="w-3.5 h-3.5" />
                  </div>
                  <span className={`text-xs font-bold ${font.id === 'Playfair Display' ? 'font-serif' : font.id === 'Space Grotesk' ? 'font-grotesk' : font.id === 'JetBrains Mono' ? 'font-mono' : 'font-sans'}`}>
                    {font.name}
                  </span>
                </div>
                <p className="text-[10px] text-gray-400 leading-normal">{font.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Theme Settings */}
        <div className="space-y-3">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Proposal Document Theme</h4>
          <div className="grid grid-cols-2 gap-3">
            {THEME_MODES.map((mode) => (
              <button
                key={mode.id}
                type="button"
                disabled={!editing}
                onClick={() => setFormData({ ...formData, themeMode: mode.id })}
                className={`p-3 border rounded-xl text-left transition-all flex items-start gap-2.5 ${
                  formData.themeMode === mode.id
                    ? 'border-indigo-600 bg-indigo-50/30 ring-2 ring-indigo-500/20'
                    : 'border-gray-200 hover:border-gray-300'
                } ${!editing && 'opacity-85'}`}
              >
                <div className="p-1.5 bg-gray-100 rounded text-gray-600 mt-0.5">
                  <mode.icon className="w-3.5 h-3.5" />
                </div>
                <div>
                  <span className="text-xs font-bold text-gray-800 block">{mode.name}</span>
                  <span className="text-[9.5px] text-gray-400 leading-tight block mt-0.5">{mode.desc}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Color Presets */}
        <div className="space-y-3">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Brand Color Scheme</h4>
          <div className="grid grid-cols-2 gap-3">
            {PRESET_SCHEMES.map((scheme) => (
              <button
                key={scheme.id}
                type="button"
                disabled={!editing}
                onClick={() => handleSchemeSelect(scheme)}
                className={`p-3 border rounded-xl text-left transition-all flex items-center gap-2.5 ${
                  formData.colorScheme === scheme.id
                    ? 'border-indigo-600 bg-indigo-50/20 ring-2 ring-indigo-500/10'
                    : 'border-gray-200 hover:border-gray-300'
                } ${!editing && 'opacity-85'}`}
              >
                <div className={`w-4 h-4 rounded-full ${scheme.preview} shrink-0`} />
                <div>
                  <span className="text-xs font-bold text-gray-800 block">{scheme.name}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Custom Color Pickers */}
          {formData.colorScheme === 'custom' && (
            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 border border-slate-200 rounded-xl animate-fade-in">
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-gray-400 uppercase">Primary Accent Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    disabled={!editing}
                    value={formData.mainColor}
                    onChange={(e) => setFormData({ ...formData, mainColor: e.target.value })}
                    className="w-8 h-8 rounded border border-gray-300 cursor-pointer overflow-hidden"
                  />
                  <input
                    type="text"
                    disabled={!editing}
                    value={formData.mainColor}
                    onChange={(e) => setFormData({ ...formData, mainColor: e.target.value })}
                    className="flex-1 text-xs p-1.5 border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 uppercase font-mono"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-gray-400 uppercase">Secondary BG Tint</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    disabled={!editing}
                    value={formData.bgColor}
                    onChange={(e) => setFormData({ ...formData, bgColor: e.target.value })}
                    className="w-8 h-8 rounded border border-gray-300 cursor-pointer overflow-hidden"
                  />
                  <input
                    type="text"
                    disabled={!editing}
                    value={formData.bgColor}
                    onChange={(e) => setFormData({ ...formData, bgColor: e.target.value })}
                    className="flex-1 text-xs p-1.5 border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 uppercase font-mono"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Live Preview Column */}
      <div className="lg:col-span-6 flex flex-col space-y-4">
        <div className="flex justify-between items-center select-none">
          <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-indigo-500 animate-pulse" /> Live Branded Document Preview
          </h4>
          <div className="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200 gap-0.5 text-[9.5px] font-bold">
            <button
              type="button"
              onClick={() => setPreviewTab('sow')}
              className={`px-2 py-1 rounded flex items-center gap-1 cursor-pointer transition-all ${
                previewTab === 'sow' ? 'bg-white text-indigo-600 shadow-3xs' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <FileText className="w-3 h-3" /> SOW Word Document
            </button>
            <button
              type="button"
              onClick={() => setPreviewTab('spreadsheet')}
              className={`px-2 py-1 rounded flex items-center gap-1 cursor-pointer transition-all ${
                previewTab === 'spreadsheet' ? 'bg-white text-emerald-600 shadow-3xs' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <FileSpreadsheet className="w-3 h-3" /> Spreadsheet Costing
            </button>
          </div>
        </div>

        {previewTab === 'sow' ? (
          /* SOW Mock Document layout */
          <div className="flex-1 bg-slate-100 border border-slate-200 rounded-2xl p-6 flex flex-col items-center justify-center min-h-[480px]">
            {/* Paper Container */}
            <div 
              className={`w-full max-w-[420px] min-h-[500px] shadow-lg border rounded-sm p-6 text-left relative transition-all duration-300 ${getFontClass(formData.fontFamily)} ${
                formData.themeMode === 'ambientDark' ? 'bg-slate-900 border-slate-800 text-slate-100' :
                formData.themeMode === 'sepia' ? 'bg-[#faf6ee] border-[#ebd9c3] text-[#433422]' :
                formData.themeMode === 'cyber' ? 'bg-neutral-950 border-emerald-900/40 text-emerald-400 font-mono' :
                'bg-white border-gray-200 text-gray-800'
              }`}
            >
              {/* Colored brand accent top bar */}
              <div 
                className="absolute top-0 left-0 right-0 h-1 transition-all" 
                style={{ backgroundColor: formData.mainColor }}
              />

              {/* Letterhead Header */}
              <div className="flex justify-between items-start border-b pb-3.5 mb-5 border-gray-100" style={{ borderColor: `${formData.mainColor}18` }}>
                <div className="flex items-center gap-2">
                  {formData.logoUrl ? (
                    <img src={formData.logoUrl} alt="Logo" className="w-8 h-8 object-contain" referrerPolicy="no-referrer" />
                  ) : (
                    <div 
                      className="w-8 h-8 rounded flex items-center justify-center font-bold text-white text-[11px] shrink-0"
                      style={{ backgroundColor: formData.mainColor }}
                    >
                      {formData.companyName ? formData.companyName.substring(0, 2).toUpperCase() : "RF"}
                    </div>
                  )}
                  <div>
                    <h5 className="font-extrabold text-[9px] uppercase tracking-wider" style={{ color: formData.themeMode === 'cyber' ? '#10b981' : formData.mainColor }}>
                      {formData.companyName || "Vendor Company"}
                    </h5>
                    <p className="text-[7.5px] opacity-50">Official Bid Respondent</p>
                  </div>
                </div>
                <div className="text-right text-[7.5px] opacity-60 font-mono">
                  <p>SOL_ID: RFP-2026-CLOUD</p>
                  <p>STATE: WASHINGTON</p>
                </div>
              </div>

              {/* Cover Summary Block */}
              <div 
                className="p-3 rounded-lg border-l-3 mb-4"
                style={{ 
                  borderLeftColor: formData.mainColor,
                  backgroundColor: formData.themeMode === 'ambientDark' ? '#1e293b' :
                                   formData.themeMode === 'sepia' ? '#eedfcb' :
                                   formData.themeMode === 'cyber' ? '#07160b' : '#f8fafc'
                }}
              >
                <h6 className="text-[10px] font-black uppercase tracking-wide mb-0.5" style={{ color: formData.themeMode === 'cyber' ? '#10b981' : '#0f172a' }}>Technical Statement of Work</h6>
                <p className="text-[9px] opacity-75 leading-relaxed">
                  We are pleased to submit this responsive proposal. We bring deep, proven domain expertise, structured operations, and certified technical specialists.
                </p>
              </div>

              {/* Styled Proposal Headings */}
              <div className="space-y-3">
                <h6 className="text-[10px] font-extrabold uppercase tracking-wider border-b pb-0.5" style={{ color: formData.mainColor, borderColor: `${formData.mainColor}20` }}>
                  1. Executive Summary & Capabilities
                </h6>
                <p className="text-[9.5px] opacity-80 leading-relaxed">
                  Our customized corporate suite bridges full-service compliance, cloud deployment registries, and secure financial management under a unified service level framework (SLA).
                </p>
                <ul className="text-[9px] space-y-1 list-disc pl-3 opacity-80">
                  <li>24/7/365 Dedicated SOW Support escalation structures</li>
                  <li>Official state and federal DBE certification validations</li>
                  <li>Real-time automated compliance verification reports</li>
                </ul>

                <h6 className="text-[10px] font-extrabold uppercase tracking-wider border-b pb-0.5 pt-2" style={{ color: formData.mainColor, borderColor: `${formData.mainColor}20` }}>
                  2. Performance Safeguards
                </h6>
                <p className="text-[9.5px] opacity-80 leading-relaxed">
                  All software deliveries are validated through a state-of-the-art continuous integration suite protecting and ensuring project milestones.
                </p>
              </div>

              {/* Document Footer */}
              <div className="border-t pt-2.5 mt-8 flex justify-between items-center text-[7px] font-mono opacity-50 border-gray-150">
                <span>CONFIDENTIAL • {formData.companyName?.toUpperCase() || "VENDOR CO."}</span>
                <span>Page 1 of 1</span>
              </div>
            </div>
          </div>
        ) : (
          /* Spreadsheet Mock layout */
          <div className="flex-1 bg-slate-100 border border-slate-200 rounded-2xl p-6 flex flex-col justify-center min-h-[480px]">
            {/* Google Sheets Layout */}
            <div className="w-full bg-white border border-slate-200 rounded-lg shadow-md overflow-hidden flex flex-col text-[10px] text-left">
              {/* Sheet Ribbon */}
              <div className="bg-slate-50 border-b border-slate-200 p-2.5 flex items-center gap-2">
                <div 
                  className="p-1 rounded text-white shrink-0"
                  style={{ backgroundColor: formData.mainColor }}
                >
                  <FileSpreadsheet className="w-3.5 h-3.5" />
                </div>
                <div>
                  <p className="font-extrabold text-slate-800 tracking-tight font-mono">RFP-2026-CLOUD_Bid_Costing.xlsx</p>
                  <p className="text-[8px] text-slate-400">Connected Excel Sheet • Auto-Calculated Formulas Active</p>
                </div>
              </div>

              {/* Grid Header */}
              <table className="w-full border-collapse font-mono text-slate-600">
                <thead>
                  <tr className="bg-slate-100 border-b border-slate-200 text-center text-[8.5px] text-slate-400 font-bold select-none divide-x divide-slate-200">
                    <th className="w-6 bg-slate-200/50" />
                    <th className="px-2 py-1 text-left">A (Item)</th>
                    <th className="px-2 py-1 text-right w-12">B (Qty)</th>
                    <th className="px-2 py-1 text-right w-20">C (Unit Price)</th>
                    <th className="px-2 py-1 text-right bg-emerald-50/40 text-emerald-900 w-24 border-l border-slate-200 font-bold">D (Total Cost)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr className="divide-x divide-slate-100">
                    <td className="w-6 bg-slate-50 text-center text-[7.5px] text-slate-300 font-bold select-none border-r border-slate-200">2</td>
                    <td className="px-2 py-1.5 font-sans font-medium text-slate-700 truncate max-w-[150px]">Gemini SOW API Credits (Million Tokens)</td>
                    <td className="px-2 py-1.5 text-right font-medium">15</td>
                    <td className="px-2 py-1.5 text-right font-medium text-slate-500">$350.00</td>
                    <td className="px-2 py-1.5 text-right bg-emerald-50/10 font-bold text-slate-800">$5,250.00</td>
                  </tr>
                  <tr className="divide-x divide-slate-100">
                    <td className="w-6 bg-slate-50 text-center text-[7.5px] text-slate-300 font-bold select-none border-r border-slate-200">3</td>
                    <td className="px-2 py-1.5 font-sans font-medium text-slate-700 truncate max-w-[150px]">SLA Support Engineering (Premium Tier)</td>
                    <td className="px-2 py-1.5 text-right font-medium">1</td>
                    <td className="px-2 py-1.5 text-right font-medium text-slate-500">$2,400.00</td>
                    <td className="px-2 py-1.5 text-right bg-emerald-50/10 font-bold text-slate-800">$2,400.00</td>
                  </tr>
                  <tr className="divide-x divide-slate-100">
                    <td className="w-6 bg-slate-50 text-center text-[7.5px] text-slate-300 font-bold select-none border-r border-slate-200">4</td>
                    <td className="px-2 py-1.5 font-sans font-medium text-slate-700 truncate max-w-[150px]">Corporate Custom Brand Dashboard Suite</td>
                    <td className="px-2 py-1.5 text-right font-medium">1</td>
                    <td className="px-2 py-1.5 text-right font-medium text-slate-500">$850.00</td>
                    <td className="px-2 py-1.5 text-right bg-emerald-50/10 font-bold text-slate-800">$850.00</td>
                  </tr>

                  {/* Header Branded Row Accent */}
                  <tr 
                    className="text-white text-[9.5px] font-bold divide-x divide-slate-200 border-t"
                    style={{ backgroundColor: formData.mainColor }}
                  >
                    <td className="w-6 text-center text-[7.5px] font-bold select-none opacity-50">5</td>
                    <td className="px-2 py-1.5 text-left font-sans uppercase tracking-wide">GRAND SUMMARY</td>
                    <td className="px-2 py-1.5 text-right">17</td>
                    <td className="px-2 py-1.5" />
                    <td className="px-2 py-1.5 text-right font-extrabold underline decoration-double text-white">
                      $8,500.00
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Tabs Footer */}
              <div className="bg-slate-100 border-t border-slate-200 px-3 py-1 flex items-center gap-1.5 text-[8px] font-bold text-slate-500">
                <span className="text-slate-300 shrink-0">➕ ☰</span>
                <div 
                  className="bg-white border-x border-t border-slate-200 rounded-t px-2.5 py-0.5 flex items-center gap-1 -mb-1"
                  style={{ color: formData.mainColor }}
                >
                  <span className="w-1 h-1 rounded-full" style={{ backgroundColor: formData.mainColor }} />
                  Summary Pricing Schedule
                </div>
                <div className="hover:bg-slate-200 px-2 py-0.5 rounded-t cursor-pointer">
                  Labor SOW Details
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
